import React, { useMemo } from 'react';
import ActiveAssetsHistogram from '@components/Widgets/ActiveAssetsHistogram';
import { AvComposedWidget } from '@components/Widgets/AvComposedWidget';
import { NoDataFoundWidget } from '@components/Widgets/layout.components';
import Widget, { chartStyle } from '@components/Widgets/Widget';
import { AvMessagePopover, FeatureFlags, Filter, severitiesStyle, SEVERITY_LABELS } from '@fabric-ui/components';
import { InfoFull } from '@fabric-ui/icons/colorful';
import { flex, shadowInsetBorder } from '@fabric-ui/theme';
import { Box, Collapse, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { useWidgetData } from '@utils/dashboardDataUtils';
import { addCustomFiltersAsExpression, cleanEmptyFilters, filterToExpression } from '@utils/filterUtils';
import stringToFilter from '@utils/stringToFIlter';
import { Cell } from 'recharts';
import { useAvContext } from '../../../context/AvContextProvider';
import useQueryObject from '../../../hooks/useQueryObjectSql';
import useQuerySql from '../../../hooks/useQuerySql';
import DraggableWidget from '../../CustomDashboards/components/DraggableWidget';
import renderWidget from '../../CustomDashboards/components/Widgets/RenderWidget';
import {
  DwQueryRequest,
  PieBasicLegendPosition,
  PieCategoryWidgetTypes,
  PieLegendStyles,
  PieSubType,
  SortDir,
  WidgetCategory,
} from '../../CustomDashboards/types';
import { useSeverity } from '../../Settings/SeveritySlaSettings/Severity/useSeverity';
import { filtersFieldToIgnore, FINDING_RISK_SCORE, FINDING_SEVERITY_SCORE } from '../ticket.types';
import { useBuildWhereClause } from '../useBuildWhereClause';
import { IEntityViewChartsProps } from './types';
import { EntityViewWidgetWrapper } from './utils';

const getAssetsByOsWidget = ({ filters, isSmallScreen }: { filters: Filter; isSmallScreen: boolean }): PieCategoryWidgetTypes => ({
  id: '',
  definition: {
    title: {
      title: 'Asset Count by Operating System',
    },
    coords: {
      x: 0,
      y: 0,
      h: 3,
      w: 0,
    },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [
    {
      filter: Object.keys(filters).length ? filters : undefined,
      select: {
        dims: [
          {
            name: 'asset.os',
          },
        ],
        metrics: [
          {
            name: 'total_assets_active_and_inactive',
            alias: 'total_assets_active_and_inactive',
          },
        ],
      },
      groupBy: ['asset.os'],
      sorting: [
        {
          name: 'total_assets_active_and_inactive',
          dir: SortDir.DESC,
        },
      ],
      top: {
        size: 5,
        groupOthers: false,
      },
      projectionId: {
        name: 'uber_assets',
        builtIn: true,
      },
      distinct: false,
      metricFilterRootProj: true,
    },
  ],
});

const getAssetBySourceQuery = (filters: Filter): DwQueryRequest => ({
  select: {
    dims: [],
    metrics: [
      { name: 'count(asset._key)', alias: 'count' },
      { name: 'explode(asset.source_names)', alias: 'source_names' },
    ],
  },
  filter: filters,
  groupBy: ['source_names'],
  projectionId: { name: 'uber_assets', builtIn: true },
  sorting: [],
});

const getAssetsByTypeWidget = ({ filters, isSmallScreen }: { filters: Filter; isSmallScreen: boolean }): PieCategoryWidgetTypes => ({
  id: '',
  definition: {
    title: { title: 'Asset Count by Type' },
    coords: { x: 0, y: 0, h: 3, w: 0 },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [
    {
      filter: Object.keys(filters).length ? filters : undefined,
      select: {
        dims: [{ name: 'asset.type' }],
        metrics: [{ name: 'total_assets_active_and_inactive', alias: 'total_assets_active_and_inactive' }],
      },
      groupBy: ['asset.type'],
      sorting: [{ name: 'total_assets_active_and_inactive', dir: SortDir.DESC }],
      top: { size: 5, groupOthers: false },
      projectionId: { name: 'uber_assets', builtIn: true },
      distinct: false,
      metricFilterRootProj: true,
    },
  ],
});

const findingCountQuery: DwQueryRequest = {
  select: {
    dims: [],
    metrics: [{ name: 'count(finding._key)', alias: 'count' }],
  },
  projectionId: { name: 'uber_findings', builtIn: true },
  sorting: [],
};

const AssetsViewCharts: React.FC<IEntityViewChartsProps> = ({
  filters: f,
  isExpanded = true,
  setIsExpanded,
  entityTypeId,
  extraFilters,
  searchFields,
}) => {
  const theme = useTheme();
  const { measurements, featureFlags } = useAvContext();
  const isNewRiskScoreEnabled = featureFlags[FeatureFlags.NewRiskScore];
  const widgetData = useWidgetData();
  const { getSeverity } = useSeverity();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1501));
  const { fieldTypeMap } = useAvContext().accountEntities;
  const filteredFilters = Object.keys(f).reduce(
    (acc, field) =>
      !filtersFieldToIgnore.includes(field) && !!f[field].length
        ? {
            ...acc,
            [field]: f[field],
          }
        : acc,
    {}
  );

  const measurementsTypeMap = measurements.visible.reduce((acc, { systemName, type }) => ({ ...acc, [systemName]: type }), {});
  const entityExtraFiltersObject = stringToFilter(extraFilters || '');
  const regularFilters = filterToExpression({ ...fieldTypeMap, ...measurementsTypeMap }, filteredFilters);

  const filters = addCustomFiltersAsExpression({
    expressionFiltersObj: cleanEmptyFilters({ and: { operands: [regularFilters, entityExtraFiltersObject] } }),
    operand: 'or',
    operator: 'contains',
    fields: searchFields,
    value: f?.search?.[0],
    fieldTypeMap,
  });

  const filtersWhereForHistogram = useBuildWhereClause({
    entityTypeId,
    filters: f.search ? { ...filteredFilters, search: f.search } : filteredFilters,
    extra: extraFilters,
    allowMetrics: true,
    searchFields,
  });
  const widgetsToShow = [getAssetsByTypeWidget({ filters, isSmallScreen }), getAssetsByOsWidget({ filters, isSmallScreen })];

  const activeHistogram = isNewRiskScoreEnabled ? widgetData.newHistogramV2 : widgetData.newHistogram;
  const histogramScoreField = isNewRiskScoreEnabled ? FINDING_RISK_SCORE : FINDING_SEVERITY_SCORE;
  const { data: assetCountBySeverityData, isLoading: assetCountBySeverityLoading } = useQuerySql({
    key: activeHistogram.sql('MAX', filtersWhereForHistogram, histogramScoreField),
    sql: activeHistogram.sql('MAX', filtersWhereForHistogram, histogramScoreField),
    options: { placeholderData: keepPreviousData },
    onSuccess: data => activeHistogram.transformData!(data).sort((a, b) => b.pureRisk - a.pureRisk),
  });

  const { data: topAssetTypeData, isLoading: topAssetTypeLoading } = useQueryObject({
    queryObject: widgetsToShow[0].requests.map(request => ({ ...request, top: { ...request.top, size: 2 } })) as DwQueryRequest[],
    options: { placeholderData: keepPreviousData },
    onSuccess: data =>
      summarizedAssetsPieOnSuccess(
        data,
        widgetsToShow[0].requests[0].select.metrics[0].name,
        widgetsToShow[0].requests[0].select.dims[0].name
      ),
  });

  const { data: topAssetOsData, isLoading: topAssetOsLoading } = useQueryObject({
    queryObject: widgetsToShow[1].requests.map(request => ({ ...request, top: { ...request.top, size: 2 } })) as DwQueryRequest[],
    options: { placeholderData: keepPreviousData, enabled: !isSmallScreen },
    onSuccess: data =>
      summarizedAssetsPieOnSuccess(
        data,
        widgetsToShow[1].requests[0].select.metrics[0].name,
        widgetsToShow[1].requests[0].select.dims[0].name
      ),
  });

  const summarizedAssetsCountOnSuccess = data =>
    data
      ?.map(item => ({ ...item, name: getSeverity(+item.pureRisk) }))
      .reduce(
        (acc, curr) => ({
          ...acc,
          [curr.name]: acc[curr.name] ? acc[curr.name] + curr.assets : curr.assets,
        }),
        {}
      );
  const summarizedAssetsPieOnSuccess = (data, dataKey, titleKey) =>
    data?.reduce(
      (acc, curr) => ({
        ...acc,
        [curr[titleKey]]: curr[dataKey],
      }),
      {}
    );

  const summaryItems = useMemo(
    () => [
      {
        title: 'Asset Count by Severity',
        data: assetCountBySeverityData,
        isLoading: assetCountBySeverityLoading,
        onSuccess: summarizedAssetsCountOnSuccess,
        showPills: true,
        getPillsStyleFunc: key => ({ ...severitiesStyle.label, ...(severitiesStyle[key]?.label || {}) }),
        getLabelFunc: key => SEVERITY_LABELS[key],
      },
      {
        title: 'Top Asset Types',
        data: topAssetTypeData,
        isLoading: topAssetTypeLoading,
      },
      {
        title: 'Top Asset OS',
        data: topAssetOsData,
        isLoading: topAssetOsLoading,
        showIcon: false,
      },
    ],
    [assetCountBySeverityData, assetCountBySeverityLoading, topAssetOsData, topAssetOsLoading, topAssetTypeData, topAssetTypeLoading]
  );
  const { isLoading: isLoadingFindingsCountQuery, data: findingsCount } = useQueryObject({
    queryObject: findingCountQuery,
  });

  const shouldShowNumOfAssetsByRiskScore = !isLoadingFindingsCountQuery && findingsCount?.[0].count > 0;

  const { isLoading: isAssetsCountBySourceLoading, data: assetsCountBySource } = useQueryObject({
    queryObject: getAssetBySourceQuery(filters),
    options: { enabled: !isLoadingFindingsCountQuery && findingsCount?.[0].count <= 0 },
  });

  return (
    <EntityViewWidgetWrapper isExpanded={isExpanded} setIsExpanded={setIsExpanded} summaryItemsProps={{ items: summaryItems }}>
      <Collapse in={isExpanded} timeout="auto">
        <Box sx={{ ...flex.row, gap: 1.5, height: '208px' }}>
          <Box className="flex-1 shrink-0">
            {shouldShowNumOfAssetsByRiskScore ? (
              <Widget
                title={activeHistogram.title}
                titleVariant="h6"
                titleMargin={1}
                queryOptions={{
                  placeholderData: keepPreviousData,
                }}
                transformData={data => activeHistogram.transformData!(data).sort((a, b) => a.pureRisk - b.pureRisk)}
                sql={activeHistogram.sql('MAX', filtersWhereForHistogram, histogramScoreField)}
                sx={{ height: '100%', pb: 0 }}>
                <ActiveAssetsHistogram />
              </Widget>
            ) : (
              <Box
                sx={{
                  ...chartStyle,
                  overflow: 'none',
                  width: '100%',
                  height: '350px',
                  minHeight: 50,
                  gap: 2,
                  p: 3,
                  pt: 2,
                  background: theme => theme.palette.white.main,
                  boxShadow: shadowInsetBorder,
                  ...(isAssetsCountBySourceLoading && { pb: 3 }),
                }}>
                {!isLoadingFindingsCountQuery ? (
                  <Box sx={{ ...flex.itemsCenter, gap: 1, mb: 2 }}>
                    <Typography variant="h6">Asset Sources Breakdown</Typography>
                    <AvMessagePopover icon={<InfoFull />} message="Assets may have multiple sources" asTooltip />
                  </Box>
                ) : (
                  <Skeleton animation="wave" variant="text" />
                )}
                {assetsCountBySource && assetsCountBySource.length > 0 ? (
                  <AvComposedWidget
                    showLegend={false}
                    data={assetsCountBySource}
                    series={[
                      {
                        type: 'bar',
                        dataKey: 'count',
                        children: assetsCountBySource.map((entry, i) => <Cell key={entry.id} fill={theme.palette.chartColors[i]} />),
                      },
                    ]}
                    xAxisKey="source_names"
                    tooltipProps={{
                      showTitle: false,
                      showTitleLegend: false,
                      showContentLegend: false,
                    }}
                    chartProps={{ margin: { bottom: 10, left: 10, right: 10 } }}
                    labels={{ y: 'Count' }}
                    sx={{ height: '100%' }}
                  />
                ) : isLoadingFindingsCountQuery ? (
                  <Skeleton animation="wave" variant="rectangular" sx={{ flex: 1 }} />
                ) : (
                  <NoDataFoundWidget showImage />
                )}
              </Box>
            )}
          </Box>
          {widgetsToShow.map(widget => (
            <Box key={widget.definition.title.title} className="relative flex-1 shrink-0 bg-surface-base-primary">
              <DraggableWidget
                widget={widget}
                customToolbar={
                  <Typography sx={{ whiteSpace: 'nowrap' }} variant="h6">
                    {widget.definition.title.title}
                  </Typography>
                }>
                {renderWidget({ widget })}
              </DraggableWidget>
            </Box>
          ))}
        </Box>
      </Collapse>
    </EntityViewWidgetWrapper>
  );
};

export default AssetsViewCharts;
